/**
 * AI Advice button + modal — shared between LiveCamera and PhotoUpload.
 * Captures canvas, sends to backend GPT-4o-mini vision, shows Hinglish stylist advice.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, X, Loader2 } from 'lucide-react';
import api from '../../utils/api';

const AIAdviceButton = ({ canvasRef, category, product, lipColor, hairColor, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [advice, setAdvice] = useState('');

  const askAI = async () => {
    if (!canvasRef?.current) return toast.error('Canvas not ready');
    setLoading(true);
    setAdvice('');
    setOpen(true);
    try {
      // Compress canvas to base64 (0.75 quality, ~200KB)
      const dataUrl = canvasRef.current.toDataURL('image/jpeg', 0.75);
      const r = await api.post('/trial-room/advice', {
        image_base64: dataUrl,
        category,
        product_name: product?.name || null,
        product_subcategory: product?.subcategory || null,
        lip_color: lipColor || null,
        hair_color: hairColor || null,
      });
      setAdvice(r.data.advice || 'AI advice unavailable');
    } catch (e) {
      const msg = e.response?.data?.detail || 'AI advice failed';
      toast.error(`❌ ${msg}`);
      setAdvice(`⚠️ ${msg}`);
    } finally { setLoading(false); }
  };

  return (
    <>
      <button
        onClick={askAI}
        data-testid="ai-advice-btn"
        className={`px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-500 via-fuchsia-500 to-pink-500 text-white text-xs font-black flex items-center gap-1 hover:brightness-110 ${className}`}>
        <Sparkles className="w-3 h-3" /> AI Advice
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="ai-advice-modal">
          <div className="max-w-lg w-full rounded-2xl border-2 border-fuchsia-400/60 bg-gradient-to-br from-slate-950 via-purple-950/80 to-fuchsia-950/60 p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black">॥ Anant AI Stylist ॥</div>
                <h3 className="text-lg font-black text-white">🤖 Style Verdict</h3>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20"><X className="w-4 h-4" /></button>
            </div>
            {loading ? (
              <div className="flex flex-col items-center py-8 text-white">
                <Loader2 className="w-10 h-10 animate-spin text-fuchsia-400" />
                <div className="mt-3 text-sm">Stylist ready ho rahi hai...</div>
                <div className="text-[10px] text-slate-400 mt-1">Analyzing your look with GPT-4o vision</div>
              </div>
            ) : (
              <div className="rounded-xl bg-black/40 border border-white/10 p-3">
                <div className="text-sm text-slate-100 whitespace-pre-wrap leading-relaxed" data-testid="ai-advice-text">
                  {advice || 'No advice yet.'}
                </div>
              </div>
            )}
            {!loading && advice && (
              <div className="mt-3 flex gap-2">
                <button onClick={askAI} className="flex-1 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-bold">🔄 Re-analyze</button>
                <button
                  onClick={() => {
                    const shareText = `🤖 Anant AI Stylist verdict on my ${category} try-on:\n\n${advice}\n\nTry yours at ${window.location.origin}/trial-room`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
                  }}
                  className="flex-1 px-3 py-2 rounded-lg bg-green-500 hover:bg-green-400 text-white text-xs font-bold">
                  📱 Share on WhatsApp
                </button>
              </div>
            )}
            <div className="mt-2 text-[9px] text-slate-500 text-center italic">Powered by GPT-4o-mini vision · Advice is subjective — trust your own style too 💫</div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIAdviceButton;
